import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import type { RouteObject } from 'react-router-dom';
import LoadingFallback from '@/components/base/LoadingFallback';

function LazyPage({ component: Comp }: { component: LazyExoticComponent<ComponentType> }) {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Comp />
    </Suspense>
  );
}

const NotFound = lazy(() => import('@/pages/NotFound'));
const Home = lazy(() => import('@/pages/home/page'));
const ServicesPage = lazy(() => import('@/pages/services/page'));
const CompanyFormationPage = lazy(() => import('@/pages/services/company-formation/page'));
const VisaImmigrationPage = lazy(() => import('@/pages/services/visa-immigration/page'));
const BusinessExpansionPage = lazy(() => import('@/pages/services/business-expansion/page'));
const PartnerNetworkPage = lazy(() => import('@/pages/partner-network/page'));
const PartnerApplicationPage = lazy(() => import('@/pages/partner-network/apply/page'));
const BlogPage = lazy(() => import('@/pages/blog/page'));
const BlogDetailPage = lazy(() => import('@/pages/blog/[id]/page'));
const FAQPage = lazy(() => import('@/pages/faq/page'));
const LoginPage = lazy(() => import('@/pages/auth/login/page'));
const SignupPage = lazy(() => import('@/pages/auth/signup/page'));
const MFASetupPage = lazy(() => import('@/pages/auth/mfa/setup/page'));
const DashboardPage = lazy(() => import('@/pages/dashboard/page'));
const ProfilePage = lazy(() => import('@/pages/dashboard/profile/page'));
const RequestsPage = lazy(() => import('@/pages/dashboard/requests/page'));
const NewRequestPage = lazy(() => import('@/pages/dashboard/requests/new/page'));
const RequestDetailPage = lazy(() => import('@/pages/dashboard/requests/[id]/page'));
const AdminPage = lazy(() => import('@/pages/admin/page'));
const PrivacyPage = lazy(() => import('@/pages/legal/privacy/page'));
const TermsPage = lazy(() => import('@/pages/legal/terms/page'));
const CookiesPage = lazy(() => import('@/pages/legal/cookies/page'));
const ContactPage = lazy(() => import('@/pages/contact/page'));
const PricingPage = lazy(() => import('@/pages/pricing/page'));
const PaymentCallbackPage = lazy(() => import('@/pages/payment/callback/page'));
const DocumentsPage = lazy(() => import('@/pages/dashboard/documents/page'));
const AboutPage = lazy(() => import('@/pages/about/page'));
const WhyLegalVioPage = lazy(() => import('@/pages/why-legalvio/page'));
const KVKKPage = lazy(() => import('@/pages/legal/kvkk/page'));
const DisclaimerPage = lazy(() => import('@/pages/legal/disclaimer/page'));
const SupportPage = lazy(() => import('@/pages/support/page'));
const FreeAnalysisPage = lazy(() => import('@/pages/free-analysis/page'));
const CountryServicePage = lazy(() => import('@/pages/services/country/[slug]/page'));
const VisaServicePage = lazy(() => import('@/pages/services/visa/[slug]/page'));
const CareersPage = lazy(() => import('@/pages/careers/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <LazyPage component={Home} />,
  },
  {
    path: '/services',
    element: <LazyPage component={ServicesPage} />,
  },
  {
    path: '/services/company-formation',
    element: <LazyPage component={CompanyFormationPage} />,
  },
  {
    path: '/services/visa-immigration',
    element: <LazyPage component={VisaImmigrationPage} />,
  },
  {
    path: '/services/business-expansion',
    element: <LazyPage component={BusinessExpansionPage} />,
  },
  {
    path: '/partner-network',
    element: <LazyPage component={PartnerNetworkPage} />,
  },
  {
    path: '/partner-network/apply',
    element: <LazyPage component={PartnerApplicationPage} />,
  },
  {
    path: '/blog',
    element: <LazyPage component={BlogPage} />,
  },
  {
    path: '/blog/:id',
    element: <LazyPage component={BlogDetailPage} />,
  },
  {
    path: '/faq',
    element: <LazyPage component={FAQPage} />,
  },
  {
    path: '/contact',
    element: <LazyPage component={ContactPage} />,
  },
  {
    path: '/auth/login',
    element: <LazyPage component={LoginPage} />,
  },
  {
    path: '/auth/signup',
    element: <LazyPage component={SignupPage} />,
  },
  {
    path: '/auth/mfa/setup',
    element: <LazyPage component={MFASetupPage} />,
  },
  {
    path: '/dashboard',
    element: <LazyPage component={DashboardPage} />,
  },
  {
    path: '/dashboard/profile',
    element: <LazyPage component={ProfilePage} />,
  },
  {
    path: '/dashboard/requests',
    element: <LazyPage component={RequestsPage} />,
  },
  {
    path: '/dashboard/requests/new',
    element: <LazyPage component={NewRequestPage} />,
  },
  {
    path: '/dashboard/requests/:id',
    element: <LazyPage component={RequestDetailPage} />,
  },
  {
    path: '/dashboard/documents',
    element: <LazyPage component={DocumentsPage} />,
  },
  {
    path: '/admin',
    element: <LazyPage component={AdminPage} />,
  },
  {
    path: '/legal/privacy',
    element: <LazyPage component={PrivacyPage} />,
  },
  {
    path: '/legal/terms',
    element: <LazyPage component={TermsPage} />,
  },
  {
    path: '/legal/cookies',
    element: <LazyPage component={CookiesPage} />,
  },
  {
    path: '/pricing',
    element: <LazyPage component={PricingPage} />,
  },
  {
    path: '/payment/callback',
    element: <LazyPage component={PaymentCallbackPage} />,
  },
  {
    path: '/about',
    element: <LazyPage component={AboutPage} />,
  },
  {
    path: '/why-legalvio',
    element: <LazyPage component={WhyLegalVioPage} />,
  },
  {
    path: '/legal/kvkk',
    element: <LazyPage component={KVKKPage} />,
  },
  {
    path: '/legal/disclaimer',
    element: <LazyPage component={DisclaimerPage} />,
  },
  {
    path: '/support',
    element: <LazyPage component={SupportPage} />,
  },
  {
    path: '/free-analysis',
    element: <LazyPage component={FreeAnalysisPage} />,
  },
  {
    path: '/services/country/:slug',
    element: <LazyPage component={CountryServicePage} />,
  },
  {
    path: '/services/visa/:slug',
    element: <LazyPage component={VisaServicePage} />,
  },
  {
    path: '/careers',
    element: <LazyPage component={CareersPage} />,
  },
  {
    path: '*',
    element: <LazyPage component={NotFound} />,
  },
];

export default routes;